import { Settings } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { formatHtml, normalizeSignature } from '../mailCompose'
import type { MailSettings } from '../mailTypes'
import { MailBodyView } from './MailBodyView'
import { MailRichEditor } from './MailRichEditor'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  settings: MailSettings
  onSave: (next: MailSettings) => void
}

function MailSettingsForm({
  settings,
  onSave,
  onClose,
}: {
  settings: MailSettings
  onSave: (next: MailSettings) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  // Kayıtlı imza varsa HTML kodu sekmesi açılır: tablo tabanlı imzalar (logo URL'li) kod üzerinden düzenlenir.
  // Kod okunabilsin diye girintili gösterilir (anlam değişmez).
  const [form, setForm] = useState(() => ({ signature_html: formatHtml(settings.signature_html) }))
  const [mode, setMode] = useState<'html' | 'visual'>(settings.signature_html.trim() ? 'html' : 'visual')
  const signatureAsHtml = mode === 'html'
  const switchMode = (next: string) => {
    if (next === 'html') setForm((f) => ({ signature_html: formatHtml(f.signature_html) }))
    setMode(next === 'html' ? 'html' : 'visual')
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      size="lg"
      icon={<Settings />}
      title={t('mail.mailbox.settings.title')}
      description={t('mail.mailbox.settings.subtitle')}
      onSubmit={(e) => {
        e.preventDefault()
        onSave({ signature_html: normalizeSignature(form.signature_html) })
        toast.success(t('mail.mailbox.settings.saved'))
        onClose()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('common.save')}</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label htmlFor="mail-signature">{t('mail.mailbox.settings.signature')}</Label>
            <Tabs value={mode} onValueChange={switchMode}>
              <TabsList size="sm">
                <TabsTrigger value="html">{t('mail.mailbox.settings.signatureHtml')}</TabsTrigger>
                <TabsTrigger value="visual">{t('mail.mailbox.settings.signatureVisual')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <p className="text-xs text-muted-foreground">
            {signatureAsHtml ? t('mail.mailbox.settings.signatureHtmlHint') : t('mail.mailbox.settings.signatureHint')}
          </p>
          {signatureAsHtml ? (
            <Textarea
              id="mail-signature"
              value={form.signature_html}
              onChange={(e) => setForm((f) => ({ ...f, signature_html: e.target.value }))}
              rows={16}
              spellCheck={false}
              wrap="off"
              className="max-h-[50vh] whitespace-pre font-mono text-xs leading-relaxed"
              aria-label={t('mail.mailbox.settings.signature')}
            />
          ) : (
            <MailRichEditor
              id="mail-signature"
              allowImages
              pasteHtmlSource
              value={form.signature_html}
              onChange={(html) => setForm((f) => ({ ...f, signature_html: html }))}
              minHeight="8rem"
              aria-label={t('mail.mailbox.settings.signature')}
            />
          )}
          {form.signature_html.trim() ? (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">{t('mail.mailbox.settings.preview')}</p>
              <MailBodyView html={form.signature_html} />
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export function MailSettingsDialog({ open, onOpenChange, settings, onSave }: Props) {
  return open ? (
    <MailSettingsForm
      key={settings.signature_html.length}
      settings={settings}
      onSave={onSave}
      onClose={() => onOpenChange(false)}
    />
  ) : null
}
